/**
 * UUID v4 helper that works on HTTP origins (e.g. ai1 at
 * `http://192.168.50.200:8080`) where `crypto.randomUUID()` is
 * unavailable because it requires a *secure context* (HTTPS or
 * localhost).
 *
 * Why this file exists
 * --------------------
 * Round 2 fix #168 used `crypto.randomUUID()` directly to assign
 * stable IDs to React Flow nodes / edges before they're saved.
 * That works on the local dev server (localhost is a secure context)
 * but throws `crypto.randomUUID is not a function` on the deployed
 * ai1 build, which is plain HTTP. The round 3 retest reopened #168
 * because the builder's "click to add Agent" path crashed on every
 * call.
 *
 * `crypto.getRandomValues()` does NOT have the secure-context
 * restriction — it's available on HTTP origins — so we use it to
 * fill 16 random bytes and format them per RFC 4122 v4. We still
 * prefer the native `crypto.randomUUID()` when it's there because
 * it's a single function call; the `getRandomValues` path is the
 * fallback for HTTP. A `Math.random`-based fallback exists for
 * environments that lack both (very old browsers / SSR), but in
 * practice the second branch always wins in the browser.
 */
export function uuidv4(): string {
  // Prefer the native API when present (HTTPS, localhost, modern Node).
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as Crypto & { randomUUID?: () => string }).randomUUID === 'function'
  ) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
  }
  // HTTP-safe path: getRandomValues works without a secure context.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    // RFC 4122 §4.4 — set version (4) and variant (10xxxxxx) bits.
    buf[6] = (buf[6]! & 0x0f) | 0x40;
    buf[8] = (buf[8]! & 0x3f) | 0x80;
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0'));
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }
  // Last-resort fallback. Not cryptographically strong, but our
  // call sites are local node IDs in a workflow builder — collision
  // risk is effectively zero at human scale.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
